import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, lockScroll, unlockScroll } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLButtonElement} [closeDropdownButton] - The close dropdown button (inline search only).
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  #userHasInteracted = false;

  #isClosing = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeSearchFinishTimeoutId = null;

  /**
   * @type {{
   *   container: HTMLElement,
   *   startX: number,
   *   startScrollLeft: number,
   *   moved: boolean,
   * } | null}
   */
  #queriesDrag = null;

  /** Ignore the synthetic click that follows a queries-row drag. */
  #queriesDragIgnoreClickUntil = 0;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
      if (this.hasAttribute('data-hide-dropdown-when-empty')) {
        this.#syncInlineSearchOpen();
      }
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.addEventListener('pointerdown', this.#handleQueriesPointerDown, { signal });
    this.addEventListener('click', this.#handleQueriesDragClick, { signal, capture: true });
    this.addEventListener('wheel', this.#handleQueriesWheel, { signal, passive: false });

    const setUserInteracted = () => {
      this.#userHasInteracted = true;
    };
    document.addEventListener('mousedown', setUserInteracted, { signal, once: true });
    document.addEventListener('keydown', setUserInteracted, { signal, once: true });
    document.addEventListener('touchstart', setUserInteracted, { signal, once: true });

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      document.addEventListener(
        'mousedown',
        (event) => {
          if (!document.documentElement.hasAttribute('data-inline-search-open')) return;

          const target = event.target;
          if (!(target instanceof Element) || this.contains(target)) return;

          this.closeSearch({ refocus: false });
        },
        { signal, capture: true }
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape') return;
          if (!document.documentElement.hasAttribute('data-inline-search-open')) return;
          this.closeSearch({ refocus: false });
        },
        { signal }
      );

      this.addEventListener(
        'focusout',
        (event) => {
          if (this.#isClosing) return;
          if (!document.documentElement.hasAttribute('data-inline-search-open')) return;

          const next = event.relatedTarget;
          if (!(next instanceof Element) || this.contains(next)) return;

          this.closeSearch({ refocus: false });
        },
        { signal }
      );

      this.refs.searchInput.addEventListener(
        'focus',
        () => {
          if (!this.#userHasInteracted) {
            this.refs.searchInput.blur();
          }
        },
        { signal }
      );
      this.refs.searchInput.addEventListener(
        'blur',
        () => {
          if (!this.#isClosing) this.removeAttribute('data-closed');
          this.#syncInlineSearchOpen();
        },
        { signal }
      );
    }

    if (!this.hasAttribute('data-hide-recently-viewed') && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #getQueriesScrollContainer(target) {
    if (!(target instanceof Element)) return null;
    const container = target.closest('.predictive-search-results__wrapper-queries');
    if (!(container instanceof HTMLElement) || !this.contains(container)) return null;
    return container;
  }

  /**
   * @param {PointerEvent} event
   */
  #handleQueriesPointerDown = (event) => {
    if (event.pointerType === 'touch' || event.button !== 0) return;

    const container = this.#getQueriesScrollContainer(event.target);
    if (!container || container.scrollWidth <= container.clientWidth) return;

    this.#queriesDrag = {
      container,
      startX: event.clientX,
      startScrollLeft: container.scrollLeft,
      moved: false,
    };

    const dragController = new AbortController();
    const opts = { signal: dragController.signal };

    const handlePointerMove = (/** @type {PointerEvent} */ moveEvent) => {
      const drag = this.#queriesDrag;
      if (!drag) return;

      const deltaX = moveEvent.clientX - drag.startX;
      if (!drag.moved && Math.abs(deltaX) < 5) return;

      if (!drag.moved) {
        drag.moved = true;
        drag.container.setAttribute('data-dragging', '');
        drag.container.setPointerCapture(moveEvent.pointerId);
      }

      moveEvent.preventDefault();
      drag.container.scrollLeft = drag.startScrollLeft - deltaX;
    };

    const handlePointerUp = (/** @type {PointerEvent} */ upEvent) => {
      dragController.abort();
      const drag = this.#queriesDrag;
      if (!drag) return;

      drag.container.removeAttribute('data-dragging');

      if (upEvent.type !== 'pointercancel' && drag.moved) {
        this.#queriesDragIgnoreClickUntil = performance.now() + 300;
      }

      this.#queriesDrag = null;
    };

    document.addEventListener('pointermove', handlePointerMove, opts);
    document.addEventListener('pointerup', handlePointerUp, opts);
    document.addEventListener('pointercancel', handlePointerUp, opts);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleQueriesDragClick = (event) => {
    if (performance.now() > this.#queriesDragIgnoreClickUntil) return;

    event.preventDefault();
    event.stopPropagation();
    this.#queriesDragIgnoreClickUntil = 0;
  };

  /**
   * @param {WheelEvent} event
   */
  #handleQueriesWheel = (event) => {
    const container = this.#getQueriesScrollContainer(event.target);
    if (!container || container.scrollWidth <= container.clientWidth) return;

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, container.scrollLeft + delta));
    if (nextScrollLeft === container.scrollLeft) return;

    event.preventDefault();
    container.scrollLeft = nextScrollLeft;
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#closeSearchFinishTimeoutId != null) {
      clearTimeout(this.#closeSearchFinishTimeoutId);
      this.#closeSearchFinishTimeoutId = null;
    }
    this.#controller.abort();

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      document.documentElement.removeAttribute('data-inline-search-open');
      unlockScroll(this);
    }
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (
      !this.hasAttribute('data-hide-recently-viewed') &&
      !this.#emptyStateLoaded &&
      RecentlyViewed.getProducts().length > 0
    ) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  #syncInlineSearchOpen() {
    if (!this.hasAttribute('data-hide-dropdown-when-empty')) return;

    const { searchInput } = this.refs;
    const open =
      !this.hasAttribute('data-closed') && (searchInput.value.length > 0 || this.matches(':focus-within'));

    searchInput.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      document.documentElement.setAttribute('data-inline-search-open', '');
      lockScroll(this);
      for (const menu of document.querySelectorAll('header-menu')) {
        if (typeof menu.dismissActiveMenu === 'function') {
          menu.dismissActiveMenu();
        }
      }
    } else {
      document.documentElement.removeAttribute('data-inline-search-open');
      unlockScroll(this);
      for (const menu of document.querySelectorAll('header-menu')) {
        if (typeof menu.dismissActiveMenu === 'function') {
          menu.dismissActiveMenu();
        }
      }
    }
  }

  handleInlineFocus = () => {
    if (!this.#userHasInteracted) return;
    if (this.hasAttribute('data-closed')) return;
    this.#syncInlineSearchOpen();
  };

  handleInlineClick = () => {
    if (!this.hasAttribute('data-hide-dropdown-when-empty')) return;
    if (!this.hasAttribute('data-closed')) return;
    this.removeAttribute('data-closed');
    this.#syncInlineSearchOpen();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.hasAttribute('data-hide-dropdown-when-empty')) {
        this.closeSearch({ refocus: true });
      } else {
        this.#resetSearch();
      }
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    } else {
      const active = document.activeElement;
      if (active instanceof HTMLElement && this.contains(active)) {
        active.blur();
      }
      this.refs.searchInput.blur();
    }
    this.#resetSearch();
  }, 100);

  /**
   * @param {{ refocus?: boolean } | Event} [options]
   */
  closeSearch = (options) => {
    const explicitOptions =
      options && typeof options === 'object' && !(options instanceof Event) ? options : null;
    const refocusAfterClose = explicitOptions ? explicitOptions.refocus !== false : false;

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      if (this.#closeSearchFinishTimeoutId != null) {
        clearTimeout(this.#closeSearchFinishTimeoutId);
        this.#closeSearchFinishTimeoutId = null;
      }

      this.#isClosing = true;
      this.setAttribute('data-closed', 'true');
      this.#syncInlineSearchOpen();
      const active = document.activeElement;
      if (active instanceof HTMLElement && this.contains(active)) {
        active.blur();
      }
      this.resetSearch(false);

      this.#closeSearchFinishTimeoutId = setTimeout(() => {
        this.#closeSearchFinishTimeoutId = null;
        if (refocusAfterClose) {
          this.refs.searchInput.focus();
        }
        this.#isClosing = false;
      }, 150);
    } else {
      this.resetSearch(false);
    }
  };

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      this.removeAttribute('data-closed');
      this.#syncInlineSearchOpen();
    }

    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, this.#normalizeFetchedResultsMarkup(resultsMarkup));

        this.#resetScrollPositions();

        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #productsContainerId() {
    const resultsId = this.dataset.resultsId || 'predictive-search-results';
    return resultsId.replace('predictive-search-results', 'predictive-search-products');
  }

  /**
   * Section HTML always uses default IDs. Rewrite them for inline search so
   * aria-controls / morph targets stay unique vs the modal instance.
   * @param {string} markup
   * @returns {string}
   */
  #normalizeFetchedResultsMarkup(markup) {
    const resultsId = this.dataset.resultsId;
    if (!resultsId || resultsId === 'predictive-search-results') return markup;

    const productsId = this.#productsContainerId();
    return markup
      .replaceAll('id="predictive-search-results"', `id="${resultsId}"`)
      .replaceAll('id="predictive-search-products"', `id="${productsId}"`);
  }

  /**
   * @param {Element} root
   */
  #rewriteResultsIds(root) {
    const resultsId = this.dataset.resultsId;
    if (!resultsId || resultsId === 'predictive-search-results') return;

    const resultsEl =
      root.id === 'predictive-search-results' ? root : root.querySelector('#predictive-search-results');
    if (resultsEl) resultsEl.id = resultsId;

    const productsEl = root.querySelector('#predictive-search-products');
    if (productsEl) productsEl.id = this.#productsContainerId();
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    if (this.hasAttribute('data-hide-dropdown-when-empty')) {
      this.#syncInlineSearchOpen();
    }

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (!this.hasAttribute('data-hide-recently-viewed') && viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    this.#rewriteResultsIds(parsedEmptySectionMarkup);
    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
